'use client';

import { c as _c } from "react/compiler-runtime";
import { type PublicUser } from '@shared/all/domains/user';
import useFollowCounts from '@shared/utils/hooks/useFollowCounts';
import usePinnedFactiiis from '@shared/utils/hooks/usePinnedFactiiis';
import useRole from '@shared/utils/hooks/useRole';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { List } from 'lucide-react';
import Head from 'next/head';
import { useState } from 'react';
import Button from '../../../components/common/Button';
import NotFound from '../../../components/common/NotFound';
import UserFactiiisList from '../../../components/factiiis/UserFactiiisList';
import Posts from '../../../components/posts/Posts';
import BlockedProfileBanner from '../../../components/profile/BlockedProfileBanner';
import ProfileSection from '../../../components/profile/ProfileSection';
import { useRegisterSearchScope, userScope } from '../../../hooks/useSearchScope';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { client, type RouterOutputs } from '../../../utils/trpc';
type MapInputs = {
  currentUser: ReturnType<typeof useUser>['currentUser'];
  followersCount: number;
  followingCount: number;
  totalFactiiiCount: number;
  pinnedFactiiis: PublicUser['pinnedFactiiis'];
};
function mapCurrentUserToUserData({
  currentUser,
  followersCount,
  followingCount,
  totalFactiiiCount,
  pinnedFactiiis
}: MapInputs): PublicUser {
  return {
    id: currentUser.id,
    username: currentUser.username,
    name: currentUser.name,
    bio: currentUser.bio,
    tag: currentUser.tag,
    // Not derivable here: own profile skips publicDetails and the auth cookie
    // carries no createdAt. Needs createdAt on CookiePayload to show.
    isNew: false,
    // Set at render instead — see isOwnProfile below.
    isFounder: false,
    isIncognito: currentUser.isIncognito,
    avatarKey: currentUser.avatarKey,
    robohash: currentUser.robohash ?? '',
    bannerKey: currentUser.bannerKey,
    followersCount,
    followingCount,
    following: true,
    blocked: false,
    blockedByUser: false,
    muted: false,
    totalFactiiiCount,
    factiiiCountPrivate: true,
    hiddenProfileHistory: false,
    pinnedFactiiis
  };
}
type ProfileProps = {
  username: string;
  initialData?: RouterOutputs['users']['publicDetails'];
  initialError: boolean;
  isEmbed: boolean;
};
const Profile = t0 => {
  const $ = _c(104);
  const {
    username,
    initialData,
    initialError: t1,
    isEmbed
  } = t0;
  const initialError = t1 === undefined ? false : t1;
  const {
    currentUser
  } = useUser();
  const {
    followersCount,
    followingCount
  } = useFollowCounts(client);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["users", "totalFactiiiCount"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = currentUser.isAuthenticated === true;
  let t4;
  if ($[1] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: _temp,
      enabled: t3,
      staleTime: Infinity
    };
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    data: t5
  } = useQuery(t4);
  const totalFactiiiCount = t5 === undefined ? 0 : t5;
  const {
    pinnedFactiiis
  } = usePinnedFactiiis(client);
  const {
    isFounder
  } = useRole(client);
  const isOwnProfile = currentUser?.username === username;
  const [showArchived, setShowArchived] = useState(false);
  let t6;
  if ($[3] !== pinnedFactiiis) {
    t6 = pinnedFactiiis.map(_temp2);
    $[3] = pinnedFactiiis;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const ownPinnedFactiiis = t6;
  let t7;
  if ($[5] !== username) {
    t7 = ["userData", username];
    $[5] = username;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== currentUser || $[8] !== followersCount || $[9] !== followingCount || $[10] !== initialData || $[11] !== initialError || $[12] !== isOwnProfile || $[13] !== ownPinnedFactiiis || $[14] !== t7 || $[15] !== totalFactiiiCount || $[16] !== username) {
    let t9;
    if ($[18] !== currentUser || $[19] !== followersCount || $[20] !== followingCount || $[21] !== isOwnProfile || $[22] !== ownPinnedFactiiis || $[23] !== totalFactiiiCount || $[24] !== username) {
      t9 = async () => {
        if (isOwnProfile) {
          return mapCurrentUserToUserData({
            currentUser,
            followersCount,
            followingCount,
            totalFactiiiCount,
            pinnedFactiiis: ownPinnedFactiiis
          });
        }
        const [error_0, result] = await client.users.publicDetails.query({
          username
        }, {
          context: {
            silenceErrors: true
          }
        });
        if (error_0) {
          throw error_0;
        }
        return result;
      };
      $[18] = currentUser;
      $[19] = followersCount;
      $[20] = followingCount;
      $[21] = isOwnProfile;
      $[22] = ownPinnedFactiiis;
      $[23] = totalFactiiiCount;
      $[24] = username;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    t8 = {
      queryKey: t7,
      retry(failureCount, error) {
        if (error.message.toLowerCase().includes("found")) {
          return false;
        }
        return failureCount < 3;
      },
      queryFn: t9,
      enabled: Boolean(username) && !initialError,
      refetchOnMount: !initialData,
      initialData
    };
    $[7] = currentUser;
    $[8] = followersCount;
    $[9] = followingCount;
    $[10] = initialData;
    $[11] = initialError;
    $[12] = isOwnProfile;
    $[13] = ownPinnedFactiiis;
    $[14] = t7;
    $[15] = totalFactiiiCount;
    $[16] = username;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const {
    isError,
    isLoading: isPageLoading,
    refetch,
    data: userData
  } = useQuery(t8);
  let t9;
  if ($[26] !== userData || $[27] !== username) {
    t9 = userData && !userData.blocked && !userData.blockedByUser ? userScope(username) : null;
    $[26] = userData;
    $[27] = username;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  useRegisterSearchScope(t9);
  let t10;
  if ($[29] !== currentUser.id || $[30] !== isEmbed || $[31] !== isFounder || $[32] !== isOwnProfile || $[33] !== ownPinnedFactiiis || $[34] !== refetch || $[35] !== userData) {
    t10 = userData && <ProfileSection loggedIn={Boolean(currentUser.id)} userData={isOwnProfile ? {
      ...userData,
      isFounder,
      pinnedFactiiis: ownPinnedFactiiis
    } : userData} refetch={refetch} isEmbed={isEmbed} />;
    $[29] = currentUser.id;
    $[30] = isEmbed;
    $[31] = isFounder;
    $[32] = isOwnProfile;
    $[33] = ownPinnedFactiiis;
    $[34] = refetch;
    $[35] = userData;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== isEmbed || $[38] !== isOwnProfile || $[39] !== ownPinnedFactiiis || $[40] !== userData || $[41] !== username) {
    t11 = !isEmbed ? {
      items: [...(userData && !userData.blocked && !userData.blockedByUser ? [{
        icon: <List height={16} width={16} />,
        text: "Factiiis",
        component: <UserFactiiisList pinnedFactiiis={isOwnProfile ? ownPinnedFactiiis : userData.pinnedFactiiis} username={username} />
      }] : [])]
    } : undefined;
    $[37] = isEmbed;
    $[38] = isOwnProfile;
    $[39] = ownPinnedFactiiis;
    $[40] = userData;
    $[41] = username;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  const t12 = !isError;
  const t13 = userData?.id !== currentUser?.id;
  let t14;
  if ($[43] !== isError) {
    t14 = isError && <NotFound item="User" />;
    $[43] = isError;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const t15 = `${userData?.name && username ? `${userData.name} (@${username})` : "Profile"} - Factiii`;
  let t16;
  if ($[45] !== t15) {
    t16 = <title>{t15}</title>;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const t17 = `View posts, replies, Factiiis, and more info about @${username}`;
  let t18;
  if ($[47] !== t17) {
    t18 = <meta name="description" content={t17} />;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const t19 = `factiii, profile, ${username}, ${userData?.name}`;
  let t20;
  if ($[49] !== t19) {
    t20 = <meta name="keywords" content={t19} />;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const t21 = `${userData?.name && username ? `${userData.name} (@${username})` : "Profile"} - Factiii`;
  let t22;
  if ($[51] !== t21) {
    t22 = <meta property="og:title" content={t21} />;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  const t23 = `${process.env.NEXT_PUBLIC_SITE_URL}/user/${username}`;
  let t24;
  if ($[53] !== t23) {
    t24 = <meta property="og:url" content={t23} />;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <meta property="og:type" content="website" />;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  const t26 = `${userData?.name && username ? `${userData.name} (@${username})` : "Profile"} - Factiii`;
  let t27;
  if ($[56] !== t26) {
    t27 = <meta property="og:title" content={t26} />;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  const t28 = `View posts, replies, Factiiis, and more info about @${username}`;
  let t29;
  if ($[58] !== t28) {
    t29 = <meta property="og:description" content={t28} />;
    $[58] = t28;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  const t30 = `${process.env.NEXT_PUBLIC_SITE_URL}/user/${username}/opengraph`;
  let t31;
  if ($[60] !== t30) {
    t31 = <meta property="og:image" content={t30} />;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <meta name="twitter:card" content="summary_large_image" />;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  const t33 = `${process.env.NEXT_PUBLIC_SITE_URL}/user/${username}`;
  let t34;
  if ($[63] !== t33) {
    t34 = <meta property="twitter:url" content={t33} />;
    $[63] = t33;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  const t35 = `${userData?.name && username ? `${userData.name} (@${username})` : "Profile"} - Factiii`;
  let t36;
  if ($[65] !== t35) {
    t36 = <meta property="twitter:title" content={t35} />;
    $[65] = t35;
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  const t37 = `View posts, replies, Factiiis, and more info about @${username}`;
  let t38;
  if ($[67] !== t37) {
    t38 = <meta property="twitter:description" content={t37} />;
    $[67] = t37;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  const t39 = `${process.env.NEXT_PUBLIC_SITE_URL}/user/${username}/opengraph`;
  let t40;
  if ($[69] !== t39) {
    t40 = <meta property="twitter:image" content={t39} />;
    $[69] = t39;
    $[70] = t40;
  } else {
    t40 = $[70];
  }
  let t41;
  if ($[71] !== t16 || $[72] !== t18 || $[73] !== t20 || $[74] !== t22 || $[75] !== t24 || $[76] !== t27 || $[77] !== t29 || $[78] !== t31 || $[79] !== t34 || $[80] !== t36 || $[81] !== t38 || $[82] !== t40) {
    t41 = <Head>{t16}{t18}{t20}{t22}{t24}{t25}{t27}{t29}{t31}{t32}{t34}{t36}{t38}{t40}</Head>;
    $[71] = t16;
    $[72] = t18;
    $[73] = t20;
    $[74] = t22;
    $[75] = t24;
    $[76] = t27;
    $[77] = t29;
    $[78] = t31;
    $[79] = t34;
    $[80] = t36;
    $[81] = t38;
    $[82] = t40;
    $[83] = t41;
  } else {
    t41 = $[83];
  }
  let t42;
  if ($[84] !== isPageLoading || $[85] !== userData || $[86] !== username) {
    t42 = !isPageLoading && userData && <BlockedProfileBanner userData={userData} username={username} />;
    $[84] = isPageLoading;
    $[85] = userData;
    $[86] = username;
    $[87] = t42;
  } else {
    t42 = $[87];
  }
  let t43;
  if ($[88] !== isOwnProfile || $[89] !== isPageLoading || $[90] !== showArchived || $[91] !== userData || $[92] !== username) {
    t43 = !isPageLoading && userData && !userData.blocked && !userData.blockedByUser && <>{isOwnProfile && <div className="flex justify-end px-2"><Button variant="CLEAR" text={showArchived ? "Back to posts" : "Show archived"} onClick={() => setShowArchived(!showArchived)} /></div>}<Posts key={showArchived ? "archived" : "posts"} params={{
        source: "user",
        username,
        ...(isOwnProfile && showArchived && {
          archived: true
        })
      }} uniqueId={showArchived ? "archived" : undefined} disableLink={false} isFullLength={false} /></>;
    $[88] = isOwnProfile;
    $[89] = isPageLoading;
    $[90] = showArchived;
    $[91] = userData;
    $[92] = username;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] !== isEmbed || $[95] !== t10 || $[96] !== t11 || $[97] !== t12 || $[98] !== t13 || $[99] !== t14 || $[100] !== t41 || $[101] !== t42 || $[102] !== t43) {
    t44 = <AppLayout topBanner={t10} leftSidebar={t11} showPostRenderOptions={t12} disableNewPost={t13} isEmbed={isEmbed}>{t14}{t41}{t42}{t43}</AppLayout>;
    $[94] = isEmbed;
    $[95] = t10;
    $[96] = t11;
    $[97] = t12;
    $[98] = t13;
    $[99] = t14;
    $[100] = t41;
    $[101] = t42;
    $[102] = t43;
    $[103] = t44;
  } else {
    t44 = $[103];
  }
  return t44;
};
export default Profile;
async function _temp() {
  const [, d] = await client.users.totalFactiiiCount.query();
  return d?.count ?? 0;
}
function _temp2(f) {
  return {
    ...f,
    spaceSlug: f.spaceSlug ?? ""
  };
}